import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { referralApis } from "@services/apis/Referral";
import { ReferralCashierVolumeEntry } from "@services/models/Referral/GetAllReferralDataModel";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const CashierReferralProgram = () => {
  const gridRef = useRef<any>(null);

  const [rows, setRows] = useState<ReferralCashierVolumeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchReferralData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await referralApis.getAllReferralData();
      const isSuccess =
        res.status === "success" || res.success === true || Array.isArray(res.data);

      if (!isSuccess) {
        setRows([]);
        setError(res.msg || "Failed to fetch referral data");
        return;
      }

      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setRows([]);
      setError(
        err?.response?.data?.msg || "Failed to fetch referral data"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferralData();
  }, [fetchReferralData]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "ID",
        field: "id",
        minWidth: 80,
        maxWidth: 100,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Campaign ID",
        field: "campaignId",
        minWidth: 120,
        maxWidth: 140,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Referrer User ID",
        field: "referrerUserId",
        minWidth: 140,
        flex: 1,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Referred User ID",
        field: "referredUserId",
        minWidth: 140,
        flex: 1,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total Volume",
        field: "totalVolume",
        minWidth: 130,
        flex: 1,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value === null || params.value === undefined
            ? "—"
            : Number(params.value).toLocaleString(),
      },
      {
        headerName: "Total Transactions",
        field: "totalTransactions",
        minWidth: 150,
        flex: 1,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
    ],
    []
  );

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter((row) =>
      [
        row.id,
        row.campaignId,
        row.referrerUserId,
        row.referredUserId,
        row.totalVolume,
        row.totalTransactions,
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .some((value) => value.includes(term))
    );
  }, [rows, searchTerm]);

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex items-center justify-between px-[20px] sticky top-0 z-10 gap-4">
        <p className="text-text-base text-[14px] font-semibold shrink-0">
          Cashier Referral Program
        </p>

        <div className="flex items-center gap-3 flex-1 justify-end">
          <GlobalTextInputField
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search ID / Campaign / User"
            prefixIcon={
              <span className="material-symbols-outlined text-text-subtle">
                search
              </span>
            }
          />
        </div>
      </div>

      <div
        className="bg-bg-page-body p-[16px]"
        style={{ height: "calc(100dvh - 70px)" }}
      >
        {loading && rows.length === 0 ? (
          <AgGridSkeleton
            columnDefs={columnDefs}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <div className="ag-theme-alpine ag-grid-12px h-full">
            <AgGridReactTable
              ref={gridRef}
              rowData={filteredRows}
              columnDefs={columnDefs}
              error={error}
              getRowId={(params) => params.data.id.toString()}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CashierReferralProgram;
